"use client";

import * as api from "@/src/api/sdk.gen";
import { driver, type DriveStep, type Config } from "driver.js";
import "driver.js/dist/driver.css";
import { useEffect, useRef } from "react";
import { useSearchParams } from "@/src/lib/next-compat";

const TOUR_STEPS: DriveStep[] = [
  {
    element: "body",
    popover: {
      title: "Welcome to inaraX",
      description:
        "inaraX is your AI-powered learning platform. We personalize lessons to your goals, quiz you to reinforce understanding, and help you earn certificates. Let's take a quick tour so you know where everything is.",
      side: "over",
      align: "center",
    },
  },
  {
    element: "[data-tour='main-content']",
    popover: {
      title: "Your learning hub",
      description:
        "This is where you'll see your courses, progress, and what to do next. Start with Courses to pick your track and continue your learning path.",
      side: "right",
      align: "start",
    },
  },
  {
    element: "[data-tour='sidebar-nav']",
    popover: {
      title: "Navigation",
      description:
        "Use the sidebar to jump between Courses and your account pages. Most of your time will be in Courses.",
      side: "right",
      align: "start",
    },
  },
  {
    element: "[data-tour='sidebar-courses']",
    popover: {
      title: "Start with Courses",
      description:
        "Courses contain lessons tailored to your goal and pace. Each lesson is proactively generated for you.",
      side: "right",
      align: "start",
    },
  },
  {
    element: "body",
    popover: {
      title: "Proactive generation",
      description:
        "Lessons like the one above are tailored to your domain and future goal. When you start a lesson, we generate content just for you—so your path is unique.",
      side: "over",
      align: "center",
    },
  },
  {
    element: "body",
    popover: {
      title: "Module quizzes",
      description:
        "At the end of each module you'll take a quiz that checks your understanding across the lessons you've completed.",
      side: "over",
      align: "center",
    },
  },
  {
    element: "body",
    popover: {
      title: "Certificates",
      description:
        "Complete a course or level to earn certificates. You'll find them in your profile once you've finished the required lessons and module quizzes.",
      side: "over",
      align: "center",
    },
  },
  {
    element: "body",
    popover: {
      title: "You're all set",
      description:
        "You can replay this tour anytime from your profile or account settings. Happy learning!",
      side: "over",
      align: "center",
      showButtons: ["next", "close"],
    },
  },
];

const PROACTIVE_LESSON_IMAGE = "/images/onboarding/proactive-lesson-preview.png";
const QUIZ_PREVIEW_IMAGE = "/images/onboarding/quiz-preview.png";
const CERTIFICATE_PREVIEW_IMAGE = "/images/onboarding/certificate-preview.png";

function applyLargeCenteredPopover(popover: { wrapper: HTMLElement }) {
  popover.wrapper.style.maxWidth = "min(92vw, 800px)";
  popover.wrapper.style.width = "92vw";
  const popoverRoot = popover.wrapper.closest("[class*='driver']");
  if (popoverRoot instanceof HTMLElement) {
    popoverRoot.classList.add("driver-popover--proactive-centered");
    popoverRoot.style.maxWidth = "min(92vw, 800px)";
    popoverRoot.style.width = "92vw";
    const centerPopover = () => {
      popoverRoot.style.position = "fixed";
      popoverRoot.style.top = "50%";
      popoverRoot.style.left = "50%";
      popoverRoot.style.transform = "translate(-50%, -50%)";
      popoverRoot.style.margin = "0";
    };
    requestAnimationFrame(centerPopover);
    setTimeout(centerPopover, 50);
  }
}

function injectProactiveLessonImage(popover: {
  wrapper: HTMLElement;
  title: HTMLElement;
}) {
  const existing = popover.wrapper.querySelector(".driver-proactive-preview");
  if (existing) existing.remove();
  applyLargeCenteredPopover(popover);
  const wrap = document.createElement("div");
  wrap.className = "driver-proactive-preview";
  wrap.setAttribute(
    "style",
    "text-align:center;margin-bottom:16px;border-radius:10px;overflow:hidden;border:1px solid #e5e7eb;line-height:0"
  );
  const img = document.createElement("img");
  img.src = PROACTIVE_LESSON_IMAGE;
  img.alt = "Lesson tailored to your domain and future goal";
  img.setAttribute(
    "style",
    "width:100%;max-width:100%;height:auto;display:block;margin:0 auto;max-height:60vh;object-fit:contain"
  );
  img.onerror = () => {
    wrap.style.display = "none";
  };
  wrap.appendChild(img);
  popover.wrapper.insertBefore(wrap, popover.title);
}

function injectQuizPreviewImage(popover: {
  wrapper: HTMLElement;
  title: HTMLElement;
}) {
  const existing = popover.wrapper.querySelector(".driver-quiz-preview");
  if (existing) existing.remove();
  applyLargeCenteredPopover(popover);
  const wrap = document.createElement("div");
  wrap.className = "driver-quiz-preview";
  wrap.setAttribute(
    "style",
    "text-align:center;margin-bottom:16px;border-radius:10px;overflow:hidden;border:1px solid #e5e7eb;line-height:0"
  );
  const img = document.createElement("img");
  img.src = QUIZ_PREVIEW_IMAGE;
  img.alt = "Module quiz at the end of each module";
  img.setAttribute(
    "style",
    "width:100%;max-width:100%;height:auto;display:block;margin:0 auto;max-height:60vh;object-fit:contain"
  );
  img.onerror = () => {
    wrap.style.display = "none";
  };
  wrap.appendChild(img);
  popover.wrapper.insertBefore(wrap, popover.title);
}

function injectCertificatePreviewImage(popover: {
  wrapper: HTMLElement;
  title: HTMLElement;
}) {
  const existing = popover.wrapper.querySelector(".driver-certificate-preview");
  if (existing) existing.remove();
  applyLargeCenteredPopover(popover);
  const wrap = document.createElement("div");
  wrap.className = "driver-certificate-preview";
  wrap.setAttribute(
    "style",
    "text-align:center;margin-bottom:16px;border-radius:10px;overflow:hidden;border:1px solid #e5e7eb;line-height:0"
  );
  const img = document.createElement("img");
  img.src = CERTIFICATE_PREVIEW_IMAGE;
  img.alt = "Certificate earned on course completion";
  img.setAttribute(
    "style",
    "width:100%;max-width:100%;height:auto;display:block;margin:0 auto;max-height:60vh;object-fit:contain"
  );
  img.onerror = () => {
    wrap.style.display = "none";
  };
  wrap.appendChild(img);
  popover.wrapper.insertBefore(wrap, popover.title);
}

async function markTourCompleted() {
  try {
    await api.updateUserTourStatusApiV1UsersMePatch({
      body: { product_tour_completed: true },
    });
  } catch {
    // Best-effort; tour still won't show again this session
  }
}

export function startOnboardingTour(navigate?: (to: string) => void) {
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/courses") {
    window.dispatchEvent(new CustomEvent("start-onboarding-tour"));
  } else if (navigate) {
    navigate("/courses?onboarding_tour=1");
  } else {
    window.location.href = "/courses?onboarding_tour=1";
  }
}

export default function OnboardingTour() {
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  const searchParams = useSearchParams();
  const forceTour = searchParams.get("onboarding_tour") === "1";

  const runDriver = (isForced: boolean) => {
    if (driverRef.current?.isActive()) {
      driverRef.current.destroy();
    }

    const config: Config = {
      showProgress: true,
      steps: TOUR_STEPS,
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Done",
      onPopoverRender: (popover, opts) => {
        if (opts.state.activeIndex === 0) {
          applyLargeCenteredPopover(popover);
          popover.wrapper.classList.add("driver-popover--welcome");
        } else if (opts.state.activeIndex === 4) {
          injectProactiveLessonImage(popover);
        } else if (opts.state.activeIndex === 5) {
          injectQuizPreviewImage(popover);
        } else if (opts.state.activeIndex === 6) {
          injectCertificatePreviewImage(popover);
        }
      },
      onDestroyed: () => {
        if (!isForced) markTourCompleted();
        if (typeof window !== "undefined" && window.location.search.includes("onboarding_tour=1")) {
          const url = new URL(window.location.href);
          url.searchParams.delete("onboarding_tour");
          window.history.replaceState({}, "", url.pathname + (url.search ? url.search : ""));
        }
      },
      onCloseClick: (_, __, opts) => {
        opts.driver.destroy();
        if (!isForced) markTourCompleted();
      },
    };

    const driverObj = driver(config);
    driverRef.current = driverObj;
    setTimeout(() => {
      if (driverRef.current) driverRef.current.drive();
    }, 400);
  };

  useEffect(() => {
    let mounted = true;

    const handleStartEvent = () => {
      runDriver(true);
    };

    window.addEventListener("start-onboarding-tour", handleStartEvent);

    void (async () => {
      if (forceTour) {
        runDriver(true);
        return;
      }

      const { data, error } = await api.getCurrentUserProfileApiV1UsersMeGet();
      if (error || !mounted) return;
      const userData = data as { product_tour_completed_at?: string | null };
      if (userData.product_tour_completed_at == null && mounted) {
        runDriver(false);
      }
    })();

    return () => {
      mounted = false;
      window.removeEventListener("start-onboarding-tour", handleStartEvent);
      if (driverRef.current?.isActive()) driverRef.current.destroy();
    };
  }, [forceTour]);

  return null;
}

